import { PutObjectCommand, S3Client } from "@aws-sdk/client-s3";
import JSZip from "jszip";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import FileDrop from "senf-atomic-design-system/src/components/core/fileDrop/FileDrop";
import Seperator from "senf-atomic-design-system/src/components/core/seperator/Seperator";
import { getFileTypeLabel } from "senf-shared/utils/helpers";
import { cn } from "../../../../../util/classNameConstructor";
import AccordionListItem, {
  type AccordionListItemData,
  type AccordionListItemGroup,
} from "../../../../core/accordionListItem/AccordionListItem";
import Button, { ButtonVariant } from "../../../../core/button/Button";
import Icon from "../../../../core/icon/Icon";
import { Input } from "../../../../core/inputs/Input";
import InfoToast from "../../../../molecules/infoToast/InfoToast";
import { useModals } from "../../../../molecules/modalStack/ModalProvider";

// File extensions for validation
const GIS_FILE_EXTENSIONS = [
  ".geojson",
  ".json",
  ".csv",
  ".shp",
  ".dbf",
  ".prj",
  ".shx", // Shapefile components
];

const BIM_FILE_EXTENSIONS = [
  ".ifc", // Primary BIM format
  ".dwg",
  ".dxf",
  ".rvt",
  ".rfa",
  ".skp",
  ".3ds",
  ".obj",
  ".fbx",
  ".dae",
];

// Interface for accordion item (minimal, not used for content)
interface UploadProgressItem extends AccordionListItemData {
  name: string;
}

const accessToken =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJqdGkiOiI1NTlmY2M0Zi00Mzk4LTQ1NDUtYjNjMi1iMmE4YzUwMjU3MDIiLCJpZCI6MTk0OTI5LCJpYXQiOjE3NDkxOTk1Njd9.aXbwP3aBfaoGGJwiJh3mwoIdZeL9BPag8kaNhZ5yGXs";

// Configuration constants for API rate limiting and resource management
// Batch polling configuration
const MAX_BATCH_SIZE = 10; // Maximum assets to check in a single batch request

// Helper function to convert string to Uint8Array
const stringToUint8Array = (str: string) => new TextEncoder().encode(str);

// Helper function to convert ArrayBuffer to hex string
const arrayBufferToHex = (buffer: ArrayBuffer) => {
  return Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
};

// Helper function to create HMAC-SHA256 signature
async function hmacSha256(key: string, message: string): Promise<string> {
  const keyData = stringToUint8Array(key);
  const messageData = stringToUint8Array(message);

  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    keyData,
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );

  const signature = await crypto.subtle.sign("HMAC", cryptoKey, messageData);
  return arrayBufferToHex(signature);
}

// Helper function to create SHA-256 hash
async function sha256(message: string): Promise<string> {
  const messageData = stringToUint8Array(message);
  const hashBuffer = await crypto.subtle.digest("SHA-256", messageData);
  return arrayBufferToHex(hashBuffer);
}

interface AssetUpload {
  file: File;
  assetId: number | null;
  assetName: string;
  customAssetName?: string | null;
  progress: number;
  uploadProgress?: number; // Separate field for upload progress (0-45%)
  showLoader: boolean;
  tilingStatus: string;
  isTiling: boolean;
  tilingPercent: number;
  uploadStatus: string;
  fileSize: string;
  fileSizeBytes: number;
  startTime: Date;
  uploadSpeed?: number; // bytes per second
  estimatedTotal?: number; // estimated total time in seconds
  currentPhase: "preparing" | "uploading" | "processing" | "complete" | "error";
  uploadStartTime?: Date;
  bytesUploaded?: number;
  detectedEpsg?: string; // detected coordinate system
  processed?: boolean; // track if asset has been processed to prevent duplicate logging
}

// Helper function to generate default asset group name
const getDefaultAssetGroupName = (): string => {
  const now = new Date();
  const day = String(now.getDate()).padStart(2, "0");
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const year = now.getFullYear();
  return `Upload ${day}/${month}/${year}`;
};

// Note: getLabel will be defined as useCallback inside the component

// Helper function to format file size
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Number.parseFloat((bytes / k ** i).toFixed(2)) + " " + sizes[i];
};

// Helper function to format speed
const formatSpeed = (bytesPerSecond: number): string => {
  return formatFileSize(bytesPerSecond) + "/s";
};

// Helper function to estimate upload time based on file size
const estimateUploadTime = (fileSizeBytes: number): number => {
  // Estimate based on average broadband speed (assuming 10 Mbps = 1.25 MB/s)
  const averageSpeedMBps = 1.25 * 1024 * 1024; // bytes per second
  const baseUploadTime = fileSizeBytes / averageSpeedMBps;
  const processingOverhead = Math.min(30, fileSizeBytes / (10 * 1024 * 1024)); // 30s max for large files
  return baseUploadTime + processingOverhead;
};

// Helper function to count BIM files in ZIP archive
const countBimFilesInZip = async (zipFile: File): Promise<number> => {
  try {
    const zip = new JSZip();
    const zipContent = await zip.loadAsync(zipFile);

    const allowedExtensions = [
      ".ifc",
      ".dwg",
      ".dxf",
      ".rvt",
      ".rfa",
      ".skp",
      ".3ds",
      ".obj",
      ".fbx",
      ".dae",
    ]; // BIM/CAD extensions

    let count = 0;
    for (const [filename, zipEntry] of Object.entries(zipContent.files)) {
      // Skip directories and hidden files
      if (
        zipEntry.dir ||
        filename.startsWith("__MACOSX/") ||
        filename.startsWith(".DS_Store")
      ) {
        continue;
      }

      // Check if file has allowed extension
      const fileExtension = filename
        .toLowerCase()
        .substring(filename.lastIndexOf("."));
      if (allowedExtensions.includes(fileExtension)) {
        count++;
      }
    }

    return count;
  } catch (error) {
    console.error(`❌ Failed to count files in ZIP ${zipFile.name}:`, error);
    return 1; // Fallback to 1 if counting fails
  }
};

// Helper function to detect if a file is a BIM file
const detectBimFileType = (file: File): boolean => {
  const fileName = file.name.toLowerCase();
  const fileExtension = fileName.substring(fileName.lastIndexOf("."));
  return BIM_FILE_EXTENSIONS.includes(fileExtension);
};

// Helper function to analyze ZIP contents for GIS files
const analyzeZipForGisContent = async (zipFile: File): Promise<boolean> => {
  try {
    const zip = new JSZip();
    const zipContent = await zip.loadAsync(zipFile);

    let gisFiles = 0;
    let bimFiles = 0;

    Object.entries(zipContent.files).forEach(([filename, zipEntry]) => {
      // Skip directories and hidden files
      if (
        zipEntry.dir ||
        filename.startsWith("__MACOSX/") ||
        filename.startsWith(".DS_Store")
      ) {
        return;
      }

      const fileExtension = filename
        .toLowerCase()
        .substring(filename.lastIndexOf("."));

      if (GIS_FILE_EXTENSIONS.includes(fileExtension)) {
        gisFiles++;
      } else if (
        [
          ".ifc",
          ".dwg",
          ".dxf",
          ".rvt",
          ".rfa",
          ".skp",
          ".3ds",
          ".obj",
          ".fbx",
          ".dae",
        ].includes(fileExtension)
      ) {
        bimFiles++;
      }
    });

    // Return true if it contains more GIS files than BIM files
    return gisFiles > bimFiles;
  } catch (error) {
    console.error(
      `❌ Failed to analyze ZIP contents for ${zipFile.name}:`,
      error,
    );
    return false; // If analysis fails, assume it's not a GIS ZIP
  }
};

// Helper function to extract BIM files from ZIP archive
const extractBimFilesFromZip = async (zipFile: File): Promise<File[]> => {
  try {
    console.log(`📦 Extracting BIM files from ZIP: ${zipFile.name}`);
    const zip = new JSZip();
    const zipContent = await zip.loadAsync(zipFile);

    const extractedFiles: File[] = [];
    const allowedExtensions = [
      ".ifc",
      ".dwg",
      ".dxf",
      ".rvt",
      ".rfa",
      ".skp",
      ".3ds",
      ".obj",
      ".fbx",
      ".dae",
    ]; // BIM/CAD extensions

    // Process each file in the ZIP
    for (const [filename, zipEntry] of Object.entries(zipContent.files)) {
      // Skip directories and hidden files
      if (
        zipEntry.dir ||
        filename.startsWith("__MACOSX/") ||
        filename.startsWith(".DS_Store")
      ) {
        continue;
      }

      // Check if file has allowed extension
      const fileExtension = filename
        .toLowerCase()
        .substring(filename.lastIndexOf("."));
      if (!allowedExtensions.includes(fileExtension)) {
        console.log(`⏭️ Skipping non-BIM file: ${filename}`);
        continue;
      }

      try {
        // Extract file content
        const fileContent = await zipEntry.async("blob");
        const extractedFile = new File([fileContent], filename, {
          type: "application/octet-stream",
        });
        extractedFiles.push(extractedFile);
        console.log(
          `✅ Extracted: ${filename} (${formatFileSize(extractedFile.size)})`,
        );
      } catch (error) {
        console.error(`❌ Failed to extract ${filename}:`, error);
      }
    }

    console.log(
      `📦 ZIP extraction complete: ${extractedFiles.length} BIM files found`,
    );
    return extractedFiles;
  } catch (error) {
    console.error(`❌ Failed to extract ZIP file ${zipFile.name}:`, error);
    throw new Error(
      `Failed to extract ZIP file: ${error instanceof Error ? error.message : "Unknown error"}`,
    );
  }
};

// Helper function to get detailed error messages
const getDetailedErrorMessage = (status: string, data?: any): string => {
  switch (status) {
    case "DATA_ERROR":
      return "❌ Data Processing Error: The uploaded file contains invalid or corrupted data. This could be due to:\n• Unsupported file format or version\n• Corrupted file during upload\n• Missing required data structures\n• Incompatible coordinate system\n\nPlease check your file and try uploading again.";

    case "ERROR":
      return "❌ General Processing Error: An unexpected error occurred during processing. This could be due to:\n• Server-side processing issues\n• File size limitations\n• Network connectivity problems\n• Temporary service unavailability\n\nPlease try again or contact support if the issue persists.";

    case "TIMEOUT":
      return "⏰ Processing Timeout: The file processing took longer than expected and timed out. This could be due to:\n• Very large file size\n• Complex 3D geometry\n• High server load\n• Network connectivity issues\n\nTry uploading a smaller file or try again later.";

    case "INVALID_FORMAT":
      return "📁 Invalid File Format: The uploaded file format is not supported. Please ensure:\n• File is a valid 3D model format (IFC, OBJ, FBX, etc.)\n• File is not corrupted\n• File version is compatible\n• File contains valid 3D geometry";

    case "QUOTA_EXCEEDED":
      return "💾 Quota Exceeded: You have reached your upload quota limit. Please:\n• Remove some existing assets\n• Upgrade your plan\n• Contact support for assistance";

    default:
      return `❌ Processing Failed: ${status}\n\nAn error occurred during file processing. Please try again or contact support if the issue persists.`;
  }
};

// Helper function to get progress bar color based on file size and speed
const getProgressBarColor = (
  fileSizeBytes: number,
  uploadSpeed?: number,
): string => {
  if (uploadSpeed && uploadSpeed > 5 * 1024 * 1024) return "bg-green-500"; // Fast upload
  if (fileSizeBytes > 100 * 1024 * 1024) return "bg-orange-500"; // Large file
  if (fileSizeBytes > 50 * 1024 * 1024) return "bg-yellow-500"; // Medium file
  return "bg-blue-500"; // Normal
};

// Helper function to get animation class based on file size
const getProgressAnimation = (
  fileSizeBytes: number,
  currentPhase: string,
): string => {
  if (currentPhase === "uploading") {
    if (fileSizeBytes > 100 * 1024 * 1024) return "animate-pulse"; // Large files pulse
    return ""; // Normal animation for smaller files
  }
  if (currentPhase === "processing") return "animate-pulse";
  return "";
};

// Helper function to detect EPSG code from file content or name
const detectEpsgCode = async (file: File): Promise<string> => {
  const defaultEpsg = "EPSG:5685"; // DB_REF system - your primary coordinate system

  try {
    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.split(".").pop() || "";

    console.log(
      `🔍 Detecting EPSG code for file: ${file.name} (${formatFileSize(file.size)})`,
    );
    console.log(`📁 File extension: ${fileExtension}`);
    console.log(`🏷️ Full filename (lowercase): ${fileName}`);

    // First check filename for EPSG patterns (most reliable)
    const epsgPatterns = [
      /epsg[_\-\s:]*(\d+)/i,
      /crs[_\-\s:]*(\d+)/i,
      /srid[_\-\s:]*(\d+)/i,
      /utm[_\-\s]*(\d+)/i,
      /zone[_\-\s]*(\d+)/i,
    ];

    for (const pattern of epsgPatterns) {
      const match = fileName.match(pattern);
      if (match) {
        const code = match[1];
        // Convert UTM zone to EPSG
        if (pattern.source.includes("utm") || pattern.source.includes("zone")) {
          const utmZone = Number.parseInt(code);
          if (utmZone >= 1 && utmZone <= 60) {
            const epsgCode =
              utmZone <= 30
                ? `EPSG:${32600 + utmZone}`
                : `EPSG:${32600 + utmZone}`;
            console.log(`🗺️ Detected UTM Zone ${utmZone} → ${epsgCode}`);
            return epsgCode;
          }
        } else {
          const detectedEpsg = `EPSG:${code}`;
          console.log(
            `✅ Detected EPSG code from filename pattern: ${detectedEpsg}`,
          );
          return detectedEpsg;
        }
      }
    }

    // Enhanced location hints with more patterns - prioritizing your project coordinate systems
    const locationHints = {
      // Primary project coordinate systems
      db_ref: "EPSG:5685",
      dbref: "EPSG:5685",
      "db-ref": "EPSG:5685",
      s_jtsk: "EPSG:5514",
      sjtsk: "EPSG:5514",
      "s-jtsk": "EPSG:5514",
      jtsk: "EPSG:5514",

      // Germany & Berlin (prefer EPSG:5685 for your projects)
      berlin: "EPSG:5685",
      germany: "EPSG:5685",
      deutschland: "EPSG:5685",
      german: "EPSG:5685",
      de: "EPSG:5685",

      // Czech Republic (EPSG:5514)
      czech: "EPSG:5514",
      czechia: "EPSG:5514",
      czech_republic: "EPSG:5514",
      ceska: "EPSG:5514",
      cz: "EPSG:5514",
      praha: "EPSG:5514",
      prague: "EPSG:5514",

      // Cross-border projects
      de_cz: "EPSG:5685", // Default to German system for cross-border
      cz_de: "EPSG:5514", // Default to Czech system if Czech comes first
      border: "EPSG:5685",
      grenze: "EPSG:5685",

      // UTM zones for Germany (fallback)
      utm32: "EPSG:25832",
      utm33: "EPSG:25833",
      utm32n: "EPSG:25832",
      utm33n: "EPSG:25833",

      // ETRS89 (fallback)
      etrs89: "EPSG:25833",
      etrs: "EPSG:25833",

      // Common systems
      wgs84: "EPSG:4326",
      wgs: "EPSG:4326",
      mercator: "EPSG:3857",
      webmercator: "EPSG:3857",

      // European systems
      lambert: "EPSG:2154",
      rd: "EPSG:28992", // Netherlands
      british: "EPSG:27700", // UK
      swiss: "EPSG:2056", // Switzerland
    };

    console.log("🔍 Checking location hints in filename...");
    for (const [hint, epsg] of Object.entries(locationHints)) {
      if (fileName.includes(hint)) {
        return epsg;
      }
      // console.log(`🔍 No match for hint "${hint}"`);
    }

    // Try to read file content for text-based files
    if (
      ["ifc", "txt", "prj", "xml", "json"].includes(fileExtension) &&
      file.size < 50 * 1024 * 1024
    ) {
      console.log("📖 Reading file content for EPSG detection...");
      try {
        const text = await file.text();
        const textSample = text.substring(0, 10000); // First 10KB
        console.log(`📄 Text sample length: ${textSample.length} characters`);

        // Look for EPSG patterns in content - prioritizing your coordinate systems
        const contentPatterns = [
          /EPSG[:\s"']*5685/gi, // Priority: DB_REF
          /EPSG[:\s"']*5514/gi, // Priority: S-JTSK
          /DB[_\-\s]*REF/gi, // DB_REF system identifier
          /S[_\-\s]*JTSK/gi, // S-JTSK system identifier
          /EPSG[:\s"']*(\d+)/gi, // General EPSG detection
          /PROJCS.*?AUTHORITY.*?EPSG.*?(\d+)/gi,
          /IFCPROJECTEDCRS.*?(\d+)/gi,
          /coordinate[_\s]*system.*?(\d+)/gi,
          /CRS.*?(\d+)/gi,
          /SRID[=:\s]*(\d+)/gi,
        ];

        for (const pattern of contentPatterns) {
          const matches = textSample.matchAll(pattern);
          for (const match of matches) {
            // Handle priority patterns first
            if (pattern.source.includes("5685")) {
              console.log(
                "✅ Detected DB_REF coordinate system (EPSG:5685) from file content",
              );
              return "EPSG:5685";
            }
            if (pattern.source.includes("5514")) {
              console.log(
                "✅ Detected S-JTSK coordinate system (EPSG:5514) from file content",
              );
              return "EPSG:5514";
            }
            if (pattern.source.includes("DB.*REF")) {
              return "EPSG:5685";
            }
            if (pattern.source.includes("S.*JTSK")) {
              return "EPSG:5514";
            }

            // Handle general EPSG codes
            const code = match[1];
            if (code && code.length >= 4 && code.length <= 5) {
              const detectedEpsg = `EPSG:${code}`;
              console.log(
                `✅ Detected EPSG code from file content: ${detectedEpsg}`,
              );
              return detectedEpsg;
            }
          }
        }

        // Special handling for IFC files
        if (fileExtension === "ifc") {
          // Look for coordinate reference system in IFC
          const ifcCrsMatch = textSample.match(
            /IFCGEOGRAPHICREFERENCEINFORMATION.*?(\d+)/i,
          );
          if (ifcCrsMatch) {
            const detectedEpsg = `EPSG:${ifcCrsMatch[1]}`;
            console.log(
              `🏗️ Detected EPSG from IFC geographic reference: ${detectedEpsg}`,
            );
            return detectedEpsg;
          }
        }
      } catch (readError) {
        console.log(
          `⚠️ Could not read file content (might be binary): ${readError}`,
        );
      }
    }

    console.log(
      `⚠️ No EPSG code detected in filename or content, using default DB_REF system: ${defaultEpsg}`,
    );
    return defaultEpsg;
  } catch (error) {
    console.error("❌ Error detecting EPSG code:", error);
    console.log(`Fallback to default EPSG: ${defaultEpsg}`);
    return defaultEpsg;
  }
};

// Helper function to detect EPSG from multiple files (for ZIP uploads with .prj files)
const detectEpsgFromMultipleFiles = async (files: File[]): Promise<string> => {
  console.log(`🔍 Analyzing ${files.length} files for EPSG detection:`);
  files.forEach((f) =>
    console.log(`  📁 ${f.name} (${formatFileSize(f.size)})`),
  );

  // Look for projection-related files first (highest priority)
  const projectionFiles = files.filter((f) => {
    const name = f.name.toLowerCase();
    return (
      name.endsWith(".prj") ||
      name.endsWith(".wkt") ||
      name.endsWith(".crs") ||
      name.includes("projection")
    );
  });

  for (const projFile of projectionFiles) {
    try {
      const content = await projFile.text();
      console.log(`📄 Reading projection file: ${projFile.name}`);
      console.log(`📄 Content preview: ${content.substring(0, 300)}...`);

      // Enhanced EPSG detection patterns for projection files
      const epsgPatterns = [
        /EPSG[[":\s,]*(\d+)/gi,
        /AUTHORITY[[":\s,]*EPSG[[":\s,]*(\d+)/gi,
        /PROJCS.*?AUTHORITY.*?(\d+)/gi,
        /GEOGCS.*?AUTHORITY.*?(\d+)/gi,
        /SRID[=:\s]*(\d+)/gi,
        /\+init=epsg:(\d+)/gi,
      ];

      for (const pattern of epsgPatterns) {
        const matches = content.matchAll(pattern);
        for (const match of matches) {
          const code = match[1];
          if (code && code.length >= 4 && code.length <= 5) {
            const detectedEpsg = `EPSG:${code}`;
            console.log(
              `✅ Detected EPSG code from ${projFile.name}: ${detectedEpsg}`,
            );
            return detectedEpsg;
          }
        }
      }
    } catch (error) {
      console.error(
        `❌ Error reading projection file ${projFile.name}:`,
        error,
      );
    }
  }

  // Look for coordinate system hints in all filenames first
  console.log("🔍 Checking all filenames for coordinate system hints...");
  for (const file of files) {
    const fileName = file.name.toLowerCase();
    console.log(`📝 Analyzing filename: ${fileName}`);

    // Enhanced filename patterns
    const patterns = [
      { regex: /epsg[_\-\s:]*(\d+)/i, type: "EPSG" },
      { regex: /utm[_\-\s]*(\d{1,2})[ns]?/i, type: "UTM" },
      { regex: /zone[_\-\s]*(\d{1,2})/i, type: "UTM" },
      { regex: /(\d{4,5})_utm/i, type: "EPSG" },
      { regex: /crs[_\-\s]*(\d+)/i, type: "EPSG" },
      { regex: /srid[_\-\s]*(\d+)/i, type: "EPSG" },
    ];

    for (const { regex, type } of patterns) {
      const match = fileName.match(regex);
      if (match) {
        const code = match[1];
        if (type === "UTM") {
          const zone = Number.parseInt(code);
          if (zone >= 1 && zone <= 60) {
            // Assume northern hemisphere for Germany/Europe
            const epsgCode = `EPSG:${32600 + zone}`;
            console.log(
              `🗺️ Detected UTM Zone ${zone} from filename → ${epsgCode}`,
            );
            return epsgCode;
          }
        } else if (type === "EPSG" && code.length >= 4) {
          const detectedEpsg = `EPSG:${code}`;

          return detectedEpsg;
        }
      }
    }
  }

  // Fallback to individual file content detection
  // console.log("🔍 Falling back to individual file content analysis...");
  for (const file of files) {
    const epsg = await detectEpsgCode(file);
    if (epsg !== "EPSG:5685") {
      // If not default, we found something
      // console.log(`📋 Using EPSG from file ${file.name}: ${epsg}`);
      return epsg;
    }
  }

  // console.log(
  //   "🔄 No EPSG detected from any file, using default DB_REF system: EPSG:5685",
  // );
  return "EPSG:5685"; // DB_REF system - your primary coordinate system
};

export interface SavedAsset {
  assetId: number;
  assetName: string;
  customAssetName?: string | null;
  detectedEpsg: string;
  fileSize: string;
  uploadDate: Date;
}

interface BimUploaderProps {
  onAssetsComplete?: (assets: SavedAsset[]) => void; // Callback when all assets are complete
  formik?: any; // Callback to close modal
  initialStagedFiles?: File[]; // Initial files to stage
  zipFileName?: string; // Original ZIP file name (without .zip) for auto-setting asset group name
  isExtracting?: boolean; // Whether ZIP extraction is happening (for loading state)
  parentExtractingFileCount?: number; // Number of files being extracted by parent
}

const BimUploader = ({
  onAssetsComplete,
  formik,
  initialStagedFiles = [],
  zipFileName,
  isExtracting = false,
  parentExtractingFileCount = 0,
}: BimUploaderProps) => {
  const { t } = useTranslation();
  const { closeModal, openModal } = useModals();
  const [uploads, setUploads] = useState<AssetUpload[]>([]);
  const [assetGroupName, setAssetGroupName] = useState<string>(""); // Asset group name
  const [manualEpsg, setManualEpsg] = useState<string>(""); // Manual override option
  const [stagedFiles, setStagedFiles] = useState<File[]>(initialStagedFiles); // Files waiting to be uploaded
  const [isUploading, setIsUploading] = useState<boolean>(false); // Upload state
  const [localIsExtracting, setLocalIsExtracting] = useState<boolean>(false); // Local extraction state for direct drops
  const [extractingFileCount, setExtractingFileCount] = useState<number>(0); // Number of files being extracted
  const [isUploadExpanded, setIsUploadExpanded] = useState<boolean>(false); // Upload details expanded state
  const [completedAssets, setCompletedAssets] = useState<SavedAsset[]>([]); // Track completed assets
  const processedAssetIds = useRef<Set<number>>(new Set()); // Track processed asset IDs to prevent duplicate logging
  const completedLogIds = useRef<Set<number>>(new Set()); // Track assets already logged as completed
  const hasAutoSetName = useRef<boolean>(false); // Track if we've already auto-set the name from ZIP

  // getLabel function exactly like in ActionBar
  const getLabel = useCallback(
    (n = 1) => {
      return t(getFileTypeLabel(n));
    },
    [t],
  );

  // Update staged files when initialStagedFiles changes
  useEffect(() => {
    if (initialStagedFiles.length > 0) {
      setStagedFiles(initialStagedFiles);
    }
  }, [initialStagedFiles]);

  // Auto-set asset group name from ZIP file name if provided (only once)
  useEffect(() => {
    if (zipFileName && !hasAutoSetName.current) {
      setAssetGroupName(zipFileName);
      hasAutoSetName.current = true; // Mark that we've auto-set the name
      console.log(`🏷️ Auto-set asset group name from ZIP file: ${zipFileName}`);
    }
  }, [zipFileName]); // Only depend on zipFileName, not assetGroupName

  // Batch status checking function - much more efficient than individual polling
  const batchCheckAssetStatuses = useCallback(async () => {
    const activeUploads = uploads.filter(
      (u) => !u.processed && u.currentPhase !== "error" && u.assetId,
    );

    if (activeUploads.length === 0) {
      return;
    }

    // Process assets in smaller batches to avoid overwhelming the API
    for (let i = 0; i < activeUploads.length; i += MAX_BATCH_SIZE) {
      const batch = activeUploads.slice(i, i + MAX_BATCH_SIZE);
      const batchAssetIds = batch.map((u) => u.assetId).filter(Boolean);

      if (batchAssetIds.length === 0) continue;

      try {
        // Try direct batch fetch first
        const response = await fetch(
          `https://api.cesium.com/v1/assets?ids=${batchAssetIds.join(",")}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              Accept: "application/json",
            },
          },
        );

        if (response.ok) {
          const batchData = await response.json();

          // Handle both response formats: {assets: [...]} or {items: [...]}
          const assets = batchData.assets || batchData.items || [];
          batch.forEach((upload) => {
            const assetData = assets.find((a: any) => a.id === upload.assetId);
            if (assetData) {
              // Call the existing status processing logic
              processAssetStatusFromBatch(upload, assetData);
            }
          });
        } else {
          throw new Error(`Batch status check failed: ${response.status}`);
        }
      } catch (error) {
        console.error("❌ Batch status check failed:", error);
        // Fallback to individual checks if needed
      }

      // Small delay between batches to be respectful
      if (i + MAX_BATCH_SIZE < activeUploads.length) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    }
  }, [uploads, accessToken]);

  // Process asset status from batch response
  const processAssetStatusFromBatch = (upload: AssetUpload, data: any) => {
    // Find the upload index
    const uploadIdx = uploads.findIndex((u) => u.assetId === upload.assetId);

    if (uploadIdx === -1) {
      return;
    }

    // Call the existing pollAssetStatus logic with the data
    // This will update the upload state properly
    handleAssetStatusUpdate(uploadIdx, data);
  };

  // Handle asset status update (extracted from existing logic)
  const handleAssetStatusUpdate = (uploadIdx: number, data: any) => {
    const upload = uploads[uploadIdx];
    if (!upload) return;

    // Handle different status responses
    const status = data.status || data.state || "UNKNOWN";
    const percentComplete = data.percentComplete || data.progress || 0;

    // Skip update if nothing has changed
    if (
      upload.tilingStatus === status &&
      upload.tilingPercent === percentComplete
    ) {
      return;
    }

    // Track completion (no logging)
    if (
      (status === "COMPLETE" ||
        percentComplete >= 100 ||
        data.tilesetReady === true) &&
      !completedLogIds.current.has(upload.assetId!)
    ) {
      completedLogIds.current.add(upload.assetId!);
    }

    const processingProgress = 52 + percentComplete * 0.48; // Processing is 48% of total (52% to 100%)

    // Determine appropriate status message based on status
    let statusMessage: string;
    if (status === "NOT_STARTED" || status === "QUEUED") {
      statusMessage = `Queued for processing (${status})`;
    } else if (status === "IN_PROGRESS") {
      statusMessage = `Processing 3D tiles... ${percentComplete}% complete`;
    } else {
      statusMessage = `Processing 3D tiles... ${percentComplete}% complete (${status})`;
    }

    setUploads((prev) =>
      prev.map((u, i) =>
        i === uploadIdx
          ? {
              ...u,
              tilingStatus: status,
              isTiling: true,
              tilingPercent: percentComplete,
              progress: Math.max(u.progress, processingProgress), // Never go backwards
              uploadStatus: statusMessage,
              currentPhase: "processing",
            }
          : u,
      ),
    );

    // Check for completion - handle multiple possible status values
    const isComplete =
      status === "COMPLETE" ||
      status === "COMPLETED" ||
      status === "READY" ||
      status === "SUCCESS" ||
      (status === "IN_PROGRESS" && percentComplete >= 100) ||
      data.tilesetReady === true ||
      data.ready === true ||
      (data.type === "3DTILES" && data.status === "COMPLETE") ||
      (data.asset && data.asset.status === "COMPLETE") ||
      (data.metadata && data.metadata.status === "COMPLETE") ||
      (data.tileset &&
        typeof data.tileset === "string" &&
        data.tileset.length > 0) ||
      (data.url &&
        typeof data.url === "string" &&
        data.url.includes("tileset.json")) ||
      (data.asset &&
        data.asset.tileset &&
        typeof data.asset.tileset === "string");

    if (isComplete) {
      // Update to complete state
      setUploads((prev) =>
        prev.map((u, i) =>
          i === uploadIdx
            ? {
                ...u,
                showLoader: false,
                isTiling: false,
                progress: 100,
                uploadStatus: "Upload and processing completed successfully!",
                currentPhase: "complete",
                processed: true,
              }
            : u,
        ),
      );

      // Track completed asset
      if (!processedAssetIds.current.has(upload.assetId!)) {
        const savedAsset: SavedAsset = {
          assetId: upload.assetId!,
          assetName: upload.assetName,
          customAssetName: upload.customAssetName,
          detectedEpsg: upload.detectedEpsg || "EPSG:5685",
          fileSize: upload.fileSize,
          uploadDate: new Date(),
        };

        processedAssetIds.current.add(upload.assetId!);

        setCompletedAssets((prev) => {
          const exists = prev.some(
            (asset) => asset.assetId === savedAsset.assetId,
          );
          if (!exists) {
            return [...prev, savedAsset];
          }
          return prev;
        });
      }
    } else if (
      status === "DATA_ERROR" ||
      status === "ERROR" ||
      status === "TIMEOUT" ||
      status === "INVALID_FORMAT" ||
      status === "QUOTA_EXCEEDED"
    ) {
      const detailedErrorMessage = getDetailedErrorMessage(data.status, data);
      setUploads((prev) =>
        prev.map((u, i) =>
          i === uploadIdx
            ? {
                ...u,
                showLoader: false,
                isTiling: false,
                uploadStatus: detailedErrorMessage,
                currentPhase: "error",
              }
            : u,
        ),
      );
    }
  };

  // Calculate overall upload progress
  const getOverallProgress = () => {
    if (uploads.length === 0) return 0;
    const totalProgress = uploads.reduce(
      (sum, upload) => sum + upload.progress,
      0,
    );
    return Math.round(totalProgress / uploads.length);
  };

  // Get upload status summary
  const getUploadStatusSummary = () => {
    if (uploads.length === 0) return { complete: 0, inProgress: 0, error: 0 };

    const complete = uploads.filter(
      (u) => u.currentPhase === "complete",
    ).length;
    const error = uploads.filter((u) => u.currentPhase === "error").length;
    const inProgress = uploads.length - complete - error;

    return { complete, inProgress, error };
  };

  // Update upload speed and progress
  const updateUploadProgress = (uploadIdx: number, bytesUploaded: number) => {
    setUploads((prev) =>
      prev.map((u, i) => {
        if (i !== uploadIdx) return u;

        // Don't update if already in processing phase
        if (u.currentPhase === "processing" || u.currentPhase === "complete") {
          return u;
        }

        const now = new Date();
        const uploadDuration = u.uploadStartTime
          ? (now.getTime() - u.uploadStartTime.getTime()) / 1000
          : 0;
        const uploadSpeed =
          uploadDuration > 0 ? bytesUploaded / uploadDuration : 0;
        const uploadPercent = bytesUploaded / u.fileSizeBytes;
        const uploadProgress = 10 + uploadPercent * 35; // 10% start + 35% for upload (up to 45%)

        return {
          ...u,
          bytesUploaded,
          uploadSpeed,
          uploadProgress: Math.min(45, uploadProgress), // Separate field for upload progress
          progress: Math.max(u.progress, Math.min(45, uploadProgress)), // Never go backwards
          uploadStatus: `Uploading... ${formatFileSize(bytesUploaded)} of ${u.fileSize} (${formatSpeed(uploadSpeed)})`,
        };
      }),
    );
  };

  // Batch status checking for all assets - replaces individual polling
  useEffect(() => {
    // Only start polling if there are uploads with asset IDs
    const activeUploads = uploads.filter(
      (u) => !u.processed && u.currentPhase !== "error" && u.assetId,
    );

    if (activeUploads.length === 0) {
      return;
    }

    // Set up batch polling for all assets
    const batchInterval = setInterval(() => {
      batchCheckAssetStatuses();
    }, 30000); // Check all assets every 60 seconds

    return () => {
      clearInterval(batchInterval);
    };
  }, [batchCheckAssetStatuses]);

  // Handle file staging (drag & drop) - with ZIP extraction for direct uploads
  const handleFileDrop = async (files: FileList | File[]) => {
    const filesArray = Array.from(files);
    console.log(`📁 Staging ${filesArray.length} file(s) for upload`);

    // First, validate files and reject unsupported formats
    const rejectedFiles: string[] = [];
    const validFiles: File[] = [];

    for (const file of filesArray) {
      // Only allow BIM files and ZIP files
      if (detectBimFileType(file)) {
        validFiles.push(file);
      } else if (file.name.toLowerCase().endsWith(".zip")) {
        // For ZIP files, check if they contain BIM data
        const isGisZip = await analyzeZipForGisContent(file);
        if (isGisZip) {
          rejectedFiles.push(file.name);
        } else {
          validFiles.push(file);
        }
      } else {
        // Reject GIS files and any other unsupported formats (images, etc.)
        rejectedFiles.push(file.name);
      }
    }

    // Show error message if unsupported files were rejected
    if (rejectedFiles.length > 0) {
      const fileList =
        rejectedFiles.length > 3
          ? `${rejectedFiles.slice(0, 3).join(", ")} ${t("global:and")} ${rejectedFiles.length - 3} ${t("global:more")}`
          : rejectedFiles.join(", ");

      const title = t("errors:fileValidation.unsupportedFormat.title");
      const message =
        rejectedFiles.length === 1
          ? t("errors:fileValidation.unsupportedFormat.singleFile", {
              fileName: rejectedFiles[0],
            })
          : t("errors:fileValidation.unsupportedFormat.multipleFiles", {
              fileList,
            });

      openModal(null, {
        title,
        description: message,
        id: "file-validation-error",
        zIndex: 10001,
      });

      // If all files were rejected, return early
      if (validFiles.length === 0) {
        return;
      }
    }

    // Check if any ZIP files need extraction
    const hasZipFiles = validFiles.some((file) =>
      file.name.toLowerCase().endsWith(".zip"),
    );

    if (hasZipFiles) {
      setLocalIsExtracting(true);

      // Count total extractable files first
      let totalExtractableFiles = 0;
      for (const file of validFiles) {
        if (file.name.toLowerCase().endsWith(".zip")) {
          const fileCount = await countBimFilesInZip(file);
          totalExtractableFiles += fileCount;
        } else {
          totalExtractableFiles += 1; // Non-ZIP files count as 1
        }
      }

      setExtractingFileCount(totalExtractableFiles);
      console.log(
        `📊 Will extract ${totalExtractableFiles} files from ${validFiles.length} input files`,
      );
    }

    // Process files and extract ZIP archives
    const processedFiles: File[] = [];
    const zipFiles = validFiles.filter((file) =>
      file.name.toLowerCase().endsWith(".zip"),
    );

    for (const file of validFiles) {
      // Check if file is a ZIP archive
      if (file.name.toLowerCase().endsWith(".zip")) {
        try {
          console.log(
            `📦 Processing BIM ZIP file in BimUploader: ${file.name}`,
          );

          // Auto-set asset group name from ZIP file name if not already set
          if (!assetGroupName.trim()) {
            const zipBaseName = file.name.replace(/\.zip$/i, "");
            if (zipFiles.length === 1) {
              // Single ZIP file - use its name
              setAssetGroupName(zipBaseName);
              console.log(`🏷️ Auto-set asset group name to: ${zipBaseName}`);
            } else {
              // Multiple ZIP files - use a generic name with timestamp
              const now = new Date();
              const day = String(now.getDate()).padStart(2, "0");
              const month = String(now.getMonth() + 1).padStart(2, "0");
              const year = now.getFullYear();
              const groupName = `ZIP Upload ${day}/${month}/${year}`;
              setAssetGroupName(groupName);
              console.log(
                `🏷️ Multiple ZIP files detected, auto-set asset group name to: ${groupName}`,
              );
            }
          }

          const extractedFiles = await extractBimFilesFromZip(file);
          processedFiles.push(...extractedFiles);
        } catch (error) {
          console.error(`❌ Failed to process ZIP file ${file.name}:`, error);
          // Add the ZIP file itself as a fallback
          processedFiles.push(file);
        }
      } else {
        // Add non-ZIP files directly
        processedFiles.push(file);
      }
    }

    if (hasZipFiles) {
      // Brief delay to ensure loading state is visible
      setTimeout(() => {
        setLocalIsExtracting(false);
        setExtractingFileCount(0); // Reset count when extraction finishes
      }, 500);
    }

    console.log(
      `📁 Processed ${processedFiles.length} total files (including extracted from ZIP)`,
    );

    // Add files to staging area (avoid duplicates)
    setStagedFiles((prev) => {
      const newFiles = processedFiles.filter(
        (newFile) =>
          !prev.some(
            (existingFile) =>
              existingFile.name === newFile.name &&
              existingFile.size === newFile.size,
          ),
      );
      return [...prev, ...newFiles];
    });
  };

  // Remove file from staging
  const removeStagedFile = (index: number) => {
    setStagedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Clear all staged files
  const clearStagedFiles = () => {
    setStagedFiles([]);
  };

  // Cancel upload and return to initial view
  const handleCancel = () => {
    setUploads([]);
    setIsUploading(false);
    setStagedFiles([]);
    setAssetGroupName("");
    setManualEpsg("");
  };

  // Upload all staged files as separate individual assets
  const handleUploadAll = async () => {
    if (stagedFiles.length === 0) return;

    setIsUploading(true);
    const filesArray = stagedFiles;

    console.log(
      `🚀 Starting upload process for ${filesArray.length} file(s) as separate assets`,
    );

    // Create upload entries for each file
    const newUploads: AssetUpload[] = filesArray.map((file, index) => {
      const startTime = new Date();
      const estimatedTotal = estimateUploadTime(file.size);
      const detectedEpsg = "EPSG:5685"; // Will be detected per file

      return {
        file,
        assetId: null,
        assetName: file.name.replace(/\.(zip|ifc)$/, ""),
        customAssetName: assetGroupName.trim() || getDefaultAssetGroupName(),
        progress: 0,
        showLoader: true,
        tilingStatus: "",
        isTiling: false,
        tilingPercent: 0,
        uploadStatus: `Preparing upload of ${file.name}...`,
        fileSize: formatFileSize(file.size),
        fileSizeBytes: file.size,
        startTime,
        estimatedTotal,
        currentPhase: "preparing",
        detectedEpsg,
      };
    });

    setUploads((prev) => [...prev, ...newUploads]);

    // Upload files with controlled concurrency to respect Cesium API limits
    const MAX_CONCURRENT_UPLOADS = 20; // Conservative limit to avoid overwhelming Cesium API

    console.log(
      `🚀 Starting controlled upload process for ${filesArray.length} files (max ${MAX_CONCURRENT_UPLOADS} concurrent)`,
    );

    // Process uploads in batches to avoid overwhelming Cesium API
    for (let i = 0; i < filesArray.length; i += MAX_CONCURRENT_UPLOADS) {
      const batch = filesArray.slice(i, i + MAX_CONCURRENT_UPLOADS);
      const batchPromises = batch.map(async (file, batchIndex) => {
        const fileIndex = i + batchIndex;
        const uploadIdx: number = uploads.length + fileIndex;

        // Add delay between uploads in the same batch to respect API limits
        if (batchIndex > 0) {
          await new Promise((resolve) => setTimeout(resolve, 2000));
        }

        console.log(
          "📤 Starting upload " +
            (fileIndex + 1) +
            "/" +
            filesArray.length +
            ": " +
            file.name,
        );

        try {
          // Detect EPSG for this specific file
          let detectedEpsg = await detectEpsgCode(file);

          // Check for manual override
          if (manualEpsg && manualEpsg.trim()) {
            const override = manualEpsg.trim().toUpperCase();
            const epsgOverride = override.startsWith("EPSG:")
              ? override
              : `EPSG:${override}`;
            console.log(
              `🔧 Manual override for ${file.name}: ${detectedEpsg} → ${epsgOverride}`,
            );
            detectedEpsg = epsgOverride;
          }

          // Keep original file name for asset, but store custom name as metadata
          const finalAssetName = file.name.replace(/\.(zip|ifc)$/, "");
          const customAssetName =
            assetGroupName.trim() || getDefaultAssetGroupName();

          // Update upload with detected EPSG and custom name
          setUploads((prev) =>
            prev.map((u, i) =>
              i === uploadIdx
                ? {
                    ...u,
                    assetName: finalAssetName,
                    customAssetName,
                    detectedEpsg,
                    uploadStatus: `Creating asset for ${file.name} with ${detectedEpsg}...`,
                  }
                : u,
            ),
          );

          // Step 1: Create asset metadata for this file
          setUploads((prev) =>
            prev.map((u, i) =>
              i === uploadIdx
                ? {
                    ...u,
                    progress: 5,
                    uploadStatus: `Creating asset for ${file.name}...`,
                  }
                : u,
            ),
          );

          const postBody = {
            name: finalAssetName,
            description: `3D model uploaded via DevCenter - ${file.name}`,
            attribution: "Uploaded via DevCenter",
            type: "3DTILES",
            options: {
              sourceType: "BIM_CAD",
              geometryCompression: "MESHOPT",
              textureFormat: "KTX2",
              inputCrs: detectedEpsg,
            },
          };

          console.log(`📋 Asset creation payload for ${finalAssetName}:`, {
            ...postBody,
            options: { ...postBody.options, inputCrs: detectedEpsg },
          });

          // Try direct fetch first, fallback to proxy if CORS fails
          let response: Response;

          try {
            response = await fetch("https://api.cesium.com/v1/assets", {
              method: "POST",
              headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
                Accept: "application/json",
              },
              body: JSON.stringify(postBody),
            });

            // Check if the response indicates a CORS issue
            if (!response.ok && response.status === 0) {
              throw new Error("CORS blocked - response status 0");
            }
          } catch (corsError) {
            // If CORS fails, use the proxy
            console.log(
              "CORS error, using proxy for asset creation:",
              corsError,
            );
            try {
              response = await fetch(
                `${import.meta.env.VITE_FIREBASE_CLOUD_FUNCTIONS_URL}/apiNoCorsFetch`,
                {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify({
                    url: "https://api.cesium.com/v1/assets",
                    method: "POST",
                    headers: {
                      Authorization: `Bearer ${accessToken}`,
                      "Content-Type": "application/json",
                      Accept: "application/json",
                    },
                    body: postBody,
                  }),
                },
              );
            } catch (proxyError) {
              console.error("Both direct fetch and proxy failed:", {
                corsError,
                proxyError,
              });

              // Check if the proxy is available and working
              try {
                const proxyHealthCheck = await fetch(
                  `${import.meta.env.VITE_FIREBASE_CLOUD_FUNCTIONS_URL}/apiNoCorsFetch`,
                  {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                      url: "https://httpbin.org/get",
                      method: "GET",
                    }),
                  },
                );
                console.log(
                  "Proxy health check result:",
                  proxyHealthCheck.status,
                  proxyHealthCheck.statusText,
                );
              } catch (healthCheckError) {
                console.error("Proxy health check failed:", healthCheckError);
              }

              throw new Error(
                "Upload failed: Both direct API call and proxy failed",
              );
            }
          }

          if (!response.ok) {
            const errorText = await response.text();
            let errorMessage: string;
            try {
              const errorData: any = JSON.parse(errorText);
              errorMessage =
                errorData.message || errorData.error || response.statusText;
            } catch (e: unknown) {
              errorMessage = errorText || response.statusText;
            }
            setUploads((prev) =>
              prev.map((u, i) =>
                i === uploadIdx
                  ? {
                      ...u,
                      showLoader: false,
                      uploadStatus: `Failed: ${errorMessage}`,
                      currentPhase: "error",
                    }
                  : u,
              ),
            );
            return; // Return from this upload promise
          }

          const data: any = await response.json();
          if (!data.assetMetadata || !data.assetMetadata.id) {
            setUploads((prev) =>
              prev.map((u, i) =>
                i === uploadIdx
                  ? {
                      ...u,
                      showLoader: false,
                      uploadStatus: "Invalid response from server",
                      currentPhase: "error",
                    }
                  : u,
              ),
            );
            return; // Return from this upload promise
          }

          setUploads((prev) =>
            prev.map((u, i) =>
              i === uploadIdx
                ? {
                    ...u,
                    assetId: data.assetMetadata.id,
                    progress: 8,
                    uploadStatus: "Starting upload...",
                    currentPhase: "uploading",
                    uploadStartTime: new Date(),
                  }
                : u,
            ),
          );

          // Step 2: Upload this file to S3 with progress tracking
          const s3Client = new S3Client({
            region: "us-east-1",
            credentials: {
              accessKeyId: data.uploadLocation.accessKey,
              secretAccessKey: data.uploadLocation.secretAccessKey,
              sessionToken: data.uploadLocation.sessionToken,
            },
            endpoint: data.uploadLocation.endpoint,
          });

          const key = `${data.uploadLocation.prefix}${file.name}`;

          setUploads((prev) =>
            prev.map((u, i) =>
              i === uploadIdx
                ? {
                    ...u,
                    uploadStatus: `Uploading ${file.name}...`,
                  }
                : u,
            ),
          );

          // Simulate upload progress for large files
          const fileBuffer = new Uint8Array(await file.arrayBuffer());

          if (file.size > 10 * 1024 * 1024) {
            // Files larger than 10MB
            const chunks = Math.ceil(file.size / (2 * 1024 * 1024)); // 2MB chunks
            for (let i = 0; i < chunks; i++) {
              const fileProgress = ((i + 1) / chunks) * file.size;
              const totalProgress = 8 + (fileProgress / file.size) * 0.37; // 37% for upload
              setUploads((prev) =>
                prev.map((u, i) =>
                  i === uploadIdx
                    ? {
                        ...u,
                        progress: Math.min(45, totalProgress),
                      }
                    : u,
                ),
              );
              await new Promise((resolve) => setTimeout(resolve, 100));
            }
          }

          const uploadParams = {
            Bucket: data.uploadLocation.bucket,
            Key: key,
            Body: fileBuffer,
            ContentType: file.type || "application/octet-stream",
          };

          try {
            const command = new PutObjectCommand(uploadParams);
            await s3Client.send(command);
          } catch (error) {
            setUploads((prev) =>
              prev.map((u, i) =>
                i === uploadIdx
                  ? {
                      ...u,
                      showLoader: false,
                      uploadStatus: `Upload failed: ${error instanceof Error ? error.message : "Unknown error"}`,
                      currentPhase: "error",
                    }
                  : u,
              ),
            );
            return; // Return from this upload promise
          }

          // File uploaded successfully
          setUploads((prev) =>
            prev.map((u, i) =>
              i === uploadIdx
                ? {
                    ...u,
                    progress: 48,
                    uploadStatus: "File uploaded, finalizing...",
                    currentPhase: "processing",
                  }
                : u,
            ),
          );

          // Step 3: Notify completion
          setUploads((prev) =>
            prev.map((u, i) =>
              i === uploadIdx
                ? {
                    ...u,
                    progress: 50,
                    uploadStatus: "Preparing 3D tile processing...",
                  }
                : u,
            ),
          );

          // Try direct fetch first, fallback to proxy if CORS fails
          let completeResponse: Response;
          try {
            completeResponse = await fetch(data.onComplete.url, {
              method: data.onComplete.method,
              headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify(data.onComplete.fields),
            });
          } catch (corsError) {
            // If CORS fails, use the proxy
            console.log("CORS error, using proxy for completion notification");
            completeResponse = await fetch(
              `${import.meta.env.VITE_FIREBASE_CLOUD_FUNCTIONS_URL}/apiNoCorsFetch`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  url: data.onComplete.url,
                  body: data.onComplete.fields,
                }),
              },
            );
          }

          if (!completeResponse.ok) {
            setUploads((prev) =>
              prev.map((u, i) =>
                i === uploadIdx
                  ? {
                      ...u,
                      showLoader: false,
                      uploadStatus: `Completion failed: ${completeResponse.statusText}`,
                      currentPhase: "error",
                    }
                  : u,
              ),
            );
            return; // Return from this upload promise
          }

          // Successfully completed upload
          setUploads((prev) =>
            prev.map((u, i) =>
              i === uploadIdx
                ? {
                    ...u,
                    progress: 52,
                    uploadStatus: "Processing 3D tiles...",
                    currentPhase: "processing",
                  }
                : u,
            ),
          );

          console.log(
            `✅ Upload completed for ${file.name}, asset ID: ${data.assetMetadata.id}`,
          );
        } catch (error) {
          setUploads((prev) =>
            prev.map((u, i) =>
              i === uploadIdx
                ? {
                    ...u,
                    showLoader: false,
                    uploadStatus: `Failed: ${error instanceof Error ? error.message : "Unknown error"}`,
                    currentPhase: "error",
                  }
                : u,
            ),
          );
        }
      });

      // Wait for current batch to complete before starting next batch
      try {
        await Promise.all(batchPromises);
        console.log(
          `✅ Batch ${Math.floor(i / MAX_CONCURRENT_UPLOADS) + 1} completed`,
        );
      } catch (error) {
        console.log(
          `⚠️ Some uploads in batch ${Math.floor(i / MAX_CONCURRENT_UPLOADS) + 1} failed:`,
          error,
        );
      }

      // Add delay between batches to respect API limits
      if (i + MAX_CONCURRENT_UPLOADS < filesArray.length) {
        console.log("⏳ Waiting 3 seconds before next batch...");
        await new Promise((resolve) => setTimeout(resolve, 3000));
      }
    }

    // Clear staged files after all uploads are initiated
    setStagedFiles([]);
    setIsUploading(false);
  };

  return (
    <div className="space-y-4 overflow-y-auto max-h-[calc(100vh-100px)]">
      <div className="space-y-4">
        {/* Show initial interface when not uploading */}
        {!isUploading && uploads.length === 0 && (
          <>
            <div className={cn("px-4", stagedFiles.length ? "pb-0" : "pb-4")}>
              <div className="flex gap-2 py-4">
                <Input
                  type="text"
                  value={assetGroupName}
                  onChange={(e) => setAssetGroupName(e.target.value)}
                  placeholder="Enter group name (optional - will use 'Upload DD/MM/YYYY' if empty)"
                  label="Asset Group Name"
                />
                <Input
                  type="text"
                  value={manualEpsg}
                  onChange={(e) => setManualEpsg(e.target.value)}
                  placeholder="e.g., 5685, 5514, or EPSG:5685"
                  label="EPSG Code"
                />
              </div>

              <FileDrop
                onDrop={handleFileDrop}
                text={
                  (isExtracting || localIsExtracting) &&
                  stagedFiles.length === 0
                    ? "Extracting files from ZIP..."
                    : "Drop your BIM/CAD files here"
                }
                icon={
                  (isExtracting || localIsExtracting) &&
                  stagedFiles.length === 0
                    ? "loading"
                    : "upload"
                }
                fileTypes={["ifc", "zip"]}
                // fileTypesText={"BIM/CAD files"}
                className="min-h-32"
                multiple
                loading={
                  (isExtracting || localIsExtracting) &&
                  stagedFiles.length === 0
                }
                stagedFiles={stagedFiles}
                onRemoveStagedFile={removeStagedFile}
                showStagedFiles={true}
                showSkeletons={
                  (isExtracting || localIsExtracting) &&
                  stagedFiles.length === 0
                }
                skeletonCount={
                  extractingFileCount || parentExtractingFileCount || 3
                } // Show skeleton for each file being processed
              />
            </div>

            {/* Upload Controls */}
            {stagedFiles.length > 0 && (
              <div className="flex justify-between items-center p-4 border-t">
                <div className="flex gap-2">
                  <Button
                    leadingIcon="trash"
                    critical
                    onClick={clearStagedFiles}
                    disabled={isUploading}
                  >
                    {t("util:discard", {
                      val: getLabel(stagedFiles.length),
                    })}{" "}
                    {stagedFiles.length > 1 ? `(${stagedFiles.length})` : ""}
                  </Button>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-sm text-gray-600">
                    {t("global:totalSize")}:{" "}
                    {formatFileSize(
                      stagedFiles.reduce((sum, file) => sum + file.size, 0),
                    )}
                  </div>
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={handleUploadAll}
                    disabled={isUploading}
                    loading={isUploading}
                    text={isUploading ? "Uploading..." : "Upload"}
                  />
                </div>
              </div>
            )}
          </>
        )}

        {/* Upload Progress Section */}
        {uploads.length > 0 &&
          (() => {
            // Create the upload progress content
            const uploadProgressContent = (
              <div className="space-y-3">
                {uploads.map((upload, idx) => (
                  <div
                    key={`${upload.assetName}-${upload.startTime.getTime()}-${idx}`}
                    className="p-3 bg-white border border-gray-200 rounded-lg"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm text-gray-500">
                            {upload.fileSize}
                          </p>
                          <div className="text-sm text-gray-500">
                            📍 {upload.detectedEpsg}
                          </div>
                        </div>
                        <h3 className="font-medium text-gray-900">
                          {upload.assetName}
                        </h3>
                      </div>

                      {upload.uploadSpeed && (
                        <div className="text-right">
                          <p className="text-sm font-medium text-blue-600">
                            {formatSpeed(upload.uploadSpeed)}
                          </p>
                          <p className="text-xs text-gray-500">Upload speed</p>
                        </div>
                      )}
                    </div>

                    {/* Dynamic Progress Bar */}
                    {upload.showLoader && (
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-700">
                            {upload.uploadStatus}
                          </span>
                          <span className="text-sm text-gray-500">
                            {Math.round(upload.progress)}%
                          </span>
                        </div>

                        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                          <div
                            className={`h-2 rounded-full transition-all duration-500 ease-out ${getProgressBarColor(
                              upload.fileSizeBytes,
                              upload.uploadSpeed,
                            )} ${getProgressAnimation(upload.fileSizeBytes, upload.currentPhase)}`}
                            style={{
                              width: `${upload.progress}%`,
                              background:
                                upload.currentPhase === "uploading" &&
                                upload.fileSizeBytes > 50 * 1024 * 1024
                                  ? "linear-gradient(90deg, #3b82f6, #1d4ed8, #3b82f6)"
                                  : undefined,
                              backgroundSize:
                                upload.currentPhase === "uploading"
                                  ? "200% 100%"
                                  : undefined,
                              animation:
                                upload.currentPhase === "uploading" &&
                                upload.fileSizeBytes > 50 * 1024 * 1024
                                  ? "gradient-shift 2s ease-in-out infinite"
                                  : undefined,
                            }}
                          />
                        </div>

                        {/* Progress Details */}
                        <div className="flex justify-between text-xs text-gray-500">
                          <span>
                            {upload.currentPhase === "preparing" &&
                              "🔄 Preparing..."}
                            {upload.currentPhase === "uploading" &&
                              "⬆️ Uploading..."}
                            {upload.currentPhase === "processing" &&
                              "⚙️ Processing..."}
                          </span>
                          {upload.estimatedTotal &&
                            upload.currentPhase !== "processing" && (
                              <span>
                                Est. {Math.round(upload.estimatedTotal)}s total
                              </span>
                            )}
                        </div>
                      </div>
                    )}

                    {/* Final Status */}
                    {!upload.showLoader && (
                      <div
                        className={`p-2 rounded text-sm ${
                          upload.currentPhase === "complete"
                            ? "bg-green-50 text-green-800 border border-green-200"
                            : "bg-red-50 text-red-800 border border-red-200"
                        }`}
                      >
                        {upload.currentPhase === "complete" ? "✅" : "❌"}{" "}
                        {upload.uploadStatus}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );

            // Create accordion groups
            const groups: AccordionListItemGroup<UploadProgressItem>[] = [
              {
                groupKey: "upload-progress",
                title: "Upload Progress",
                subtitle: `${getUploadStatusSummary().complete}/${uploads.length} complete`,
                items: [], // Empty since we use custom content
                isDefaultExpanded: isUploadExpanded,
                actions: (
                  <>
                    <div className="w-32 bg-gray-200 rounded-full h-2 overflow-hidden">
                      <div
                        className="h-2 bg-blue-600 rounded-full transition-all duration-500 ease-out"
                        style={{ width: `${getOverallProgress()}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-gray-700">
                      {getOverallProgress()}%
                    </span>
                  </>
                ),
                customContent: uploadProgressContent,
              },
            ];

            return (
              <div className="p-4">
                <AccordionListItem
                  groups={groups}
                  controlled={true}
                  expandedGroups={
                    isUploadExpanded ? new Set(["upload-progress"]) : new Set()
                  }
                  onToggleGroup={(groupKey) => {
                    if (groupKey === "upload-progress") {
                      setIsUploadExpanded(!isUploadExpanded);
                    }
                  }}
                />
              </div>
            );
          })()}

        {/* Sticky Bottom Button Area */}
        {uploads.length > 0 && (
          <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4">
            <div className="flex items-center justify-end gap-3 px-4 pb-4">
              <Button
                onClick={handleCancel}
                text={t("global:back")}
                size="lg"
              />
              {(() => {
                const allComplete = uploads.every(
                  (u) =>
                    u.currentPhase === "complete" || u.currentPhase === "error",
                );
                const hasErrors = uploads.some(
                  (u) => u.currentPhase === "error",
                );
                const hasInProgress = uploads.some(
                  (u) =>
                    u.currentPhase === "preparing" ||
                    u.currentPhase === "uploading" ||
                    u.currentPhase === "processing",
                );

                if (hasInProgress) {
                  return (
                    <Button
                      variant="primary"
                      disabled={true}
                      size="lg"
                      text="Uploading..."
                    />
                  );
                }
                if (allComplete) {
                  return (
                    <Button
                      variant="primary"
                      size="lg"
                      onClick={() => {
                        // Pass all completed assets to the parent component
                        if (onAssetsComplete && completedAssets.length > 0) {
                          if (formik.values.mapStyle !== "cesium") {
                            formik.setFieldValue("mapStyle", "cesium");
                          }
                          onAssetsComplete(completedAssets);
                        }
                        closeModal({ id: "upload-modal" });
                      }}
                      text={
                        hasErrors ? "Done (with errors)" : t("global:insert")
                      }
                    />
                  );
                }
                return null;
              })()}
            </div>
          </div>
        )}
      </div>

      {/* CSS for gradient animation */}
      <style>{`
            @keyframes gradient-shift {
              0% { background-position: 0% 50%; }
              50% { background-position: 100% 50%; }
              100% { background-position: 0% 50%; }
            }
          `}</style>
    </div>
  );
};

export default BimUploader;
